import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProductsTab = ({ providerId }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    if (providerId) {
      fetchProducts();
    }
  }, [providerId]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        ?.from('provider_products')
        ?.select('*')
        ?.eq('provider_id', providerId)
        ?.eq('status', 'active')
        ?.order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setProducts(data || []);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', ...new Set(products?.map(p => p?.category)?.filter(Boolean))];

  const filteredProducts = selectedCategory === 'all'
    ? products
    : products?.filter(p => p?.category === selectedCategory);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Producten laden...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-error/10 border border-error/20 rounded-lg p-6 text-center">
        <Icon name="AlertCircle" size={32} className="text-error mx-auto mb-2" />
        <p className="text-error">{error}</p>
      </div>
    );
  }

  if (products?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-12 text-center">
        <Icon name="Package" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-xl font-bold text-foreground mb-2">Geen producten beschikbaar</h3>
        <p className="text-muted-foreground">Deze aanbieder heeft nog geen producten gepubliceerd.</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="space-y-6"
    >
      {/* Category Filter */}
      {categories?.length > 1 && (
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex flex-wrap gap-2">
            {categories?.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedCategory === category
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                {category === 'all' ? 'Alle categorieën' : category}
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Products Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProducts?.map((product, idx) => (
          <motion.div
            key={product?.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: idx * 0.1 }}
            className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-elevation-2 transition-shadow"
          >
            {/* Product Image */}
            {product?.image_url ? (
              <div className="aspect-video bg-muted overflow-hidden">
                <img
                  src={product?.image_url}
                  alt={product?.product_name || 'Product afbeelding'}
                  className="w-full h-full object-cover"
                />
              </div>
            ) : (
              <div className="aspect-video bg-muted flex items-center justify-center">
                <Icon name="Package" size={48} className="text-muted-foreground" />
              </div>
            )}

            {/* Product Info */}
            <div className="p-4">
              {product?.category && (
                <span className="inline-block px-2 py-1 bg-primary/10 text-primary text-xs rounded-full mb-2">
                  {product?.category}
                </span>
              )}
              <h3 className="text-lg font-heading font-bold text-foreground mb-2 line-clamp-2">
                {product?.product_name}
              </h3>
              <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                {product?.description}
              </p>

              {/* Specifications */}
              {product?.specifications && (
                <div className="mb-4 space-y-1">
                  {Object.entries(product?.specifications)?.slice(0, 3)?.map(([key, value]) => (
                    <div key={key} className="flex items-center text-xs">
                      <Icon name="Check" size={12} className="text-success mr-2" />
                      <span className="text-muted-foreground">{key}: {value}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Price */}
              {product?.price_range && (
                <div className="mb-4">
                  <p className="text-lg font-bold text-primary">{product?.price_range}</p>
                </div>
              )}

              {/* Action Button */}
              <Button
                variant="outline"
                size="sm"
                fullWidth
                iconName="MessageSquare"
                iconPosition="left"
              >
                Informatie aanvragen
              </Button>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

export default ProductsTab;